import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useToggle } from '@uidotdev/usehooks';

import { AuthTemplate } from '@components/auth-template';
import { Input } from '@components/input';
import { useDebounce } from '@hooks/use-debounce';
import { login, getUser } from '@service/current-user';
import { SIGN_IN_CONFIG } from '@pages/sign-in/constants/form';
import { TSignInData } from '@models/sign-in-data';

import { Box, Modal, Typography } from '@mui/material';
import { Icon } from '@components/icon';
import * as S from './styles';

export function SignIn() {
  const [open, toggleModal] = useToggle(false);
  const [modalText, setModalText] = React.useState(""); 
  const navigate = useNavigate();

  const form = useForm<TSignInData>(SIGN_IN_CONFIG);

  const userQuery = useQuery({
    queryKey: ['getUser'],
    queryFn: getUser,
  });

  if (userQuery.isSuccess) {
    navigate('/operation-dashboard');
  }

  const handleLoginError = (error: any) => {
    if (!!error.response && (error?.response?.status === 400 || error?.response?.status === 401))
    {
      setModalText("Usuário não cadastrado, inativo ou senha incorreta");
    }
    else
    {
      setModalText("Erro de conexão, verifique a conexão e tente novamente!");
    }

    toggleModal(); 
  };

  const mutation = useMutation({
    mutationFn: login,
    onError: handleLoginError,

    onSuccess: () => {
      navigate('/operation-dashboard');
    },
  });


  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSubmit();
    }
  };

  const onSubmit = useDebounce(() => {
    form.handleSubmit((data: TSignInData) => mutation.mutate(data))();
  }, 600);

  return (
    <>
      <AuthTemplate
        title="Que bom que você veio!"
        buttonText="Entrar"
        buttonOnClick={onSubmit}
        isLoading={mutation.isPending}
      >
        <S.Description lineHeight={1} mb={4}>
          Insira seus dados e acesse a plataforma para melhorar ainda mais a
          performance da sua operação.
        </S.Description>
        <Input
          name="username"
          control={form.control}
          label="usuário ou email"
          variant="outlined"
          iconName="user"
          size="small"
          fullWidth
          sx={{ mb: 3 }}
          onKeyDown={onKeyDown}
        />
        <Input
          name="password"
          control={form.control}
          label="senha"
          variant="outlined"
          iconName="password"
          size="small"
          fullWidth
          type="password"
          onKeyDown={onKeyDown}
        />
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => navigate('/forgot-password')}
          >
            Esqueci minha senha
          </Typography>
        </Box>
      </AuthTemplate>
      <Modal open={open} onClose={toggleModal}>
        <S.ModalContainer>
          <Box
            maxWidth={200}
            justifyContent="center"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Icon disableTheming name="error" alt="error icon" width={36} />
            <Typography
              variant="h5"
              color="primary"
              fontWeight={600}
              textAlign="center"
              mt={2}
            >
              {modalText}
            </Typography>
            <S.Button onClick={toggleModal}>OK</S.Button>
          </Box>
        </S.ModalContainer>
      </Modal>
    </>
  );
}
